import { Injectable } from '@angular/core';
import type {
  LlmDirectorySearchAnnotation,
  LlmDirectorySearchAnnotationSegment,
  LlmDirectorySearchAnnotationSegmentContext,
  SearchUser,
} from '@freelancer/datastore/collections';
import { QueryParams } from '@freelancer/ui/link';
import type { UserSearchAnnotation } from './user-search-annotation.types';
import type {
  UserSearchCardAnnotation,
  UserSearchCardAnnotationSegment,
} from './user-search-card-annotation.types';

/**
 * Service responsible for transforming directory search annotations from the
 * backend into a format suitable for display in the user search card.
 *
 * This service handles:
 * - Creating user annotation maps from backend data and loading status
 * - Grouping segments by text for duplicate handling
 * - Splitting annotation text to identify and link segments
 * - Deduplicating links to avoid confusion
 * - Creating appropriate links based on segment context
 */
@Injectable({
  providedIn: 'root',
})
export class UserSearchAnnotationService {
  /**
   * Transforms a UserSearchAnnotation from the backend into a
   * UserSearchCardAnnotation suitable for rendering in the UI.
   */
  createUserSearchCardAnnotation(
    userAnnotation: UserSearchAnnotation,
    user?: SearchUser,
  ): UserSearchCardAnnotation {
    const empty = {
      state: 'empty',
      segments: [],
    } as const;

    switch (userAnnotation.state) {
      case 'loading':
        return {
          state: 'loading',
        } as const;
      case 'active': {
        const { annotation } = userAnnotation;

        if (annotation === undefined) {
          return empty;
        }

        /** Segments grouped by text. Used to handle duplicated segment text */
        const segmentsGroupedByText: Partial<
          Record<string, readonly LlmDirectorySearchAnnotationSegment[]>
        > = {};

        for (const segment of annotation.segments) {
          const segmentText = segment.text;

          if (!segmentsGroupedByText[segmentText]) {
            segmentsGroupedByText[segmentText] = [];
          }

          segmentsGroupedByText[segmentText] = [
            ...segmentsGroupedByText[segmentText],
            segment,
          ];
        }

        const segmentTexts = Object.keys(segmentsGroupedByText).sort(
          (a, b) => b.length - a.length,
        ); // Sort by length desc to handle longer segments first to avoid splitting them into smaller segments

        // If no segments, just return the annotation text without any links
        if (segmentTexts.length === 0) {
          if (annotation.text.length === 0) {
            return empty;
          }

          return {
            state: 'active',
            segments: [{ type: 'text', text: annotation.text }],
          } as const;
        }

        let parts = [
          {
            text: annotation.text,
            matched: false,
          },
        ];

        for (const segmentText of segmentTexts) {
          const escaped = segmentText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`(${escaped})`, 'g');

          parts = parts.flatMap(part => {
            // If the part is already matched,
            // skip splitting it again to avoid splitting a longer segment into smaller segments
            if (part.matched) {
              return part;
            }

            const textSplits = part.text.split(regex);

            const foundSegmentIndex = textSplits.indexOf(segmentText);

            if (foundSegmentIndex === -1) {
              return part;
            }

            const foundSegment = textSplits.at(foundSegmentIndex);

            if (foundSegment === undefined) {
              return part;
            }

            return [
              {
                text: textSplits.slice(0, foundSegmentIndex).join(''),
                matched: false,
              },
              // Only keep the splits up to the first match, and mark the matched segment
              {
                text: foundSegment,
                matched: true,
              },
              {
                text: textSplits.slice(foundSegmentIndex + 1).join(''),
                matched: false,
              },
            ];
          });
        }

        const segments: readonly UserSearchCardAnnotationSegment[] = parts
          .map(part => part.text)
          .filter(part => part.length > 0) // Skip empty string, it has no effect in the UI
          .map(part => {
            const segmentsForPart = segmentsGroupedByText[part];

            // Consume the segment in the front,
            // Keep the segment if there are no other segments with the same text, the succeeding matching part will use it
            if (segmentsForPart !== undefined) {
              if (segmentsForPart.length > 1) {
                segmentsGroupedByText[part] = segmentsForPart.slice(1);
              }

              const segment = segmentsForPart?.at(0);
              // segment should never be undefined here, just to satisfy typescript
              if (segment !== undefined) {
                const link = this.createLinkForSegmentContext(
                  segment.context,
                  user,
                );

                if (link !== undefined) {
                  return {
                    type: 'link',
                    text: part,
                    link,
                    context: segment.context,
                  } as const;
                }
              }
            }

            return { type: 'text', text: part } as const;
          });

        // Convert subsequent duplicated link segments into text segments to avoid having multiple links
        // with the same text which can be confusing in the UI, only keep the first one as a link
        const registerLinks = new Set<string>();
        const dedupedSegments = segments.map(segment => {
          if (segment.type !== 'link') {
            return segment;
          }

          const { link } = segment;

          const url = `${link.path}?${new URLSearchParams(link.queryParams ?? {}).toString()}`;

          if (registerLinks.has(url)) {
            return {
              type: 'text',
              text: segment.text,
            } as const;
          }

          registerLinks.add(url);
          return segment;
        });

        const segment = dedupedSegments.at(0);
        if (segment !== undefined && dedupedSegments.length > 0) {
          return {
            state: 'active',
            segments: [segment, ...dedupedSegments.slice(1)],
          } as const;
        }

        return empty;
      }
      default:
        return empty;
    }
  }

  /**
   * Creates user annotations from the backend annotations and the users that
   * are eligible to be annotated.
   *
   * This method:
   * - Emits an 'active' state carrying the annotation for every backend
   *   annotation, keyed by its user ID
   * - Emits a 'loading' state for users in `usersToAnnotate` whose annotation
   *   has not arrived yet, only when the request is still in-flight
   * - Omits users that have no annotation once the request has completed
   *   (successfully or with an error).
   *
   * @param usersToAnnotate - Users that are eligible for annotation
   * @param annotations - Array of annotations from the backend
   * @param hasError - Whether the annotations failed to load
   * @param isComplete - Whether the datastore request has finished
   * @returns A partial record mapping user IDs to their UserSearchAnnotation states
   */
  createUserAnnotationsMap(
    usersToAnnotate: readonly SearchUser[],
    annotations: readonly LlmDirectorySearchAnnotation[],
    hasError: boolean,
    isComplete: boolean,
  ): Partial<Record<number, UserSearchAnnotation>> {
    const annotationUserIds = new Set(
      annotations.map(annotation => annotation.userId),
    );
    const pendingUsersToAnnotate = usersToAnnotate.filter(
      user => !annotationUserIds.has(user.id),
    );

    return Object.fromEntries([
      ...annotations.map(annotation => {
        const userAnnotation = {
          state: 'active',
          annotation,
        } satisfies UserSearchAnnotation;

        return [annotation.userId, userAnnotation];
      }),
      ...(!hasError && !isComplete
        ? pendingUsersToAnnotate.map(user => {
            const userAnnotation = {
              state: 'loading',
            } satisfies UserSearchAnnotation;

            return [user.id, userAnnotation];
          })
        : []),
    ]) as Partial<Record<number, UserSearchAnnotation>>;
  }

  /**
   * Creates a link URL based on the segment context type.
   */
  private createLinkForSegmentContext(
    segmentContext: LlmDirectorySearchAnnotationSegmentContext,
    user?: SearchUser,
  ): { path: string; queryParams?: QueryParams } | undefined {
    switch (segmentContext.type) {
      case 'showcase':
        return {
          path: `/portfolio-items/${segmentContext.id}`,
        };
      case 'review': {
        if (user === undefined) {
          return undefined;
        }

        return {
          path: `/u/${user.username}`,
          queryParams: {
            review_project_id: segmentContext.id,
          },
        };
      }
      default:
        return undefined;
    }
  }
}
